import { blowOf } from "./blows.ts";
import { BAR_SLOTS, BLOWS, ITEM_ID_PATTERN, RULES_ID_PATTERN, type Blow, type Rules } from "./types.ts";

/**
 * The player's bar (ADR-121 decisions 1, 2 and 4): the record a character's arranged bar is kept and sent as, the one
 * parser every boundary reads it through, and the default bar a character holds until it arranges its own. The record
 * names things, never grants them: the map server checks every use against what the character may use, so nothing
 * here is read against the rules tables, and an id the running rules lack is kept so a later rules change can restore
 * its slot.
 */

/**
 * The record's version (ADR-121 decision 4.3). It moves only when what a slot means changes.
 */
export const BAR_RECORD_VERSION = 1;

/**
 * The most bytes of a record's JSON text (ADR-121 decision 4.2): the bound `characters_bar_shape` checks on the column's
 * text, and past which the parser answers unreadable. A well-formed record reaches 617 at most.
 */
export const MAX_BAR_RECORD_BYTES = 1024;

/**
 * What a slot may hold (ADR-121 decision 1.1): a blow of the held weapon, an ability by its rules id, or an item by its
 * item id.
 */
export const BAR_SLOT_KINDS = ["blow", "ability", "item"] as const;
export type BarSlotKind = (typeof BAR_SLOT_KINDS)[number];

/**
 * One slot: empty, or a pair of its kind and the id it names.
 */
export type BarSlot = readonly ["blow", Blow] | readonly ["ability", string] | readonly ["item", string] | null;

/**
 * A player's bar: its version and exactly `BAR_SLOTS` slots, slot 1 first.
 */
export interface BarRecord {
  readonly v: typeof BAR_RECORD_VERSION;
  readonly slots: readonly BarSlot[];
}

/**
 * What `parseBarRecord` answers: the record, or unreadable with why.
 */
export type BarRead = { readonly ok: true; readonly record: BarRecord } | { readonly ok: false; readonly reason: string };

/**
 * The record's fields, the only ones a readable record has.
 */
const RECORD_FIELDS = ["v", "slots"] as const;

/**
 * Reads a value as a bar record (ADR-121 decision 4.3), as the column's driver or a frame's parse decoded it. It never
 * throws: a value whose JSON text is past `MAX_BAR_RECORD_BYTES` bytes, or that JSON cannot write, a version it does not
 * know, a field it does not know, a slot count other than `BAR_SLOTS`, a kind it does not know or an id out of its
 * kind's pattern is unreadable. Duplicates, empty slots and ids the rules lack are readable.
 *
 * @param value The decoded value.
 * @returns The record, sharing no array with `value`, or unreadable with the first reason found.
 * @example
 * parseBarRecord({ v: 1, slots: [["blow", "basic"], null, null, null, null, null, null, null, null, null] }).ok;
 * // true
 */
export function parseBarRecord(value: unknown): BarRead {
  const bytes = jsonBytes(value);
  if (bytes === null) {
    return unreadable("the value has no JSON text");
  }
  if (bytes > MAX_BAR_RECORD_BYTES) {
    return unreadable(`the value is ${bytes} bytes, past ${MAX_BAR_RECORD_BYTES}`);
  }
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return unreadable("the value is not an object");
  }
  const fields = Object.keys(value);
  const unknown = fields.find((field) => !(RECORD_FIELDS as readonly string[]).includes(field));
  if (unknown !== undefined) {
    return unreadable(`the field ${JSON.stringify(unknown)} is not a bar record's`);
  }
  const { v, slots } = value as { readonly v?: unknown; readonly slots?: unknown };
  if (v !== BAR_RECORD_VERSION) {
    return unreadable(`the version ${String(v)} is not ${BAR_RECORD_VERSION}`);
  }
  if (!Array.isArray(slots) || slots.length !== BAR_SLOTS) {
    return unreadable(`the slots are not a list of ${BAR_SLOTS}`);
  }
  const read: BarSlot[] = [];
  for (let index = 0; index < BAR_SLOTS; index += 1) {
    const slot = slotOf(slots[index] as unknown);
    if (slot === undefined) {
      return unreadable(`slot ${index + 1} is not empty, a blow, an ability or an item`);
    }
    read.push(slot);
  }
  return { ok: true, record: { v: BAR_RECORD_VERSION, slots: read } };
}

/**
 * The default bar as a record (ADR-121 decisions 2.3 and 2.4; ADR-091 decision 14): the starting weapon's blows in the
 * order of `BLOWS`, then every given ability in the order the abilities table holds them, the rest empty, and the last
 * slot the item slot. It is what a bar never arranged shows and what the page writes down at a player's first change.
 * The validator holds it to `BAR_SLOTS` (`checkBarSize`).
 *
 * @param rules The rules.
 * @param item The item the item slot derives from the bag (the first consumable in slot order), or null for none.
 * @returns The record.
 * @example
 * defaultBar(loadRules(), "minor_healing_potion").slots.slice(0, 3);
 * // [["blow", "basic"], ["blow", "heavy"], ["ability", "evasion"]]
 */
export function defaultBar(rules: Rules, item: string | null): BarRecord {
  const slots = new Array<BarSlot>(BAR_SLOTS).fill(null);
  let next = 0;
  for (const blow of BLOWS) {
    if (blowOf(rules, rules.startingWeapon, blow) !== null) {
      slots[next] = ["blow", blow];
      next += 1;
    }
  }
  for (const ability of rules.tables.abilities) {
    if (ability.given) {
      slots[next] = ["ability", ability.id];
      next += 1;
    }
  }
  slots[BAR_SLOTS - 1] = item === null ? null : ["item", item];
  return { v: BAR_RECORD_VERSION, slots };
}

/**
 * One slot read: null, or a pair whose kind is known and whose id fits its kind's pattern.
 *
 * @param value The slot as decoded.
 * @returns The slot, or undefined when it is not one.
 */
function slotOf(value: unknown): BarSlot | undefined {
  if (value === null) {
    return null;
  }
  if (!Array.isArray(value) || value.length !== 2) {
    return undefined;
  }
  const [kind, id] = value as readonly unknown[];
  if (typeof id !== "string") {
    return undefined;
  }
  if (kind === "blow") {
    const blow = BLOWS.find((name) => name === id);
    return blow === undefined ? undefined : ["blow", blow];
  }
  if (kind === "ability") {
    return RULES_ID_PATTERN.test(id) ? ["ability", id] : undefined;
  }
  if (kind === "item") {
    return ITEM_ID_PATTERN.test(id) ? ["item", id] : undefined;
  }
  return undefined;
}

/**
 * The UTF-8 bytes of a value's JSON text.
 *
 * @param value The value.
 * @returns The count, or null when JSON cannot write the value (a cycle, a bigint) or writes nothing for it.
 */
function jsonBytes(value: unknown): number | null {
  let text: string | undefined;
  try {
    text = JSON.stringify(value) as string | undefined;
  } catch {
    return null;
  }
  if (text === undefined) {
    return null;
  }
  let bytes = 0;
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    if (code < 0x80) {
      bytes += 1;
    } else if (code < 0x800) {
      bytes += 2;
    } else if (code >= 0xd800 && code < 0xdc00 && index + 1 < text.length) {
      bytes += 4;
      index += 1;
    } else {
      bytes += 3;
    }
  }
  return bytes;
}

/**
 * An unreadable answer.
 *
 * @param reason Why.
 * @returns The answer.
 */
function unreadable(reason: string): BarRead {
  return { ok: false, reason };
}
